import React, { useMemo, useState } from 'react';
import {
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import RoutePaths from '../routes/RoutePaths';
import SortableTableHeaderCell from './SortableTableHeaderCell';
import { applySortDirection, compareNumbers, compareStrings } from './tableSort';

function getMediaDetailPath(mediaId) {
    return RoutePaths.monitoring.media.replace(':mediaId', encodeURIComponent(mediaId));
}

function CountCell({ value, color }) {
    const isHighlighted = Number(value) > 0 && color;
    return (
        <TableCell align="right">
            <Typography
                variant="body2"
                sx={{
                    fontWeight: isHighlighted ? 600 : 400,
                    color: isHighlighted ? color : 'text.primary',
                }}
            >
                {value ?? 0}
            </Typography>
        </TableCell>
    );
}

const MEDIA_SORT_COLUMNS = [
    { id: 'name', labelId: 'monitoring.column.media' },
    { id: 'sources_count', labelId: 'monitoring.column.sourcesTotal', align: 'right' },
    { id: 'sources_error_count', labelId: 'monitoring.column.sourcesError', align: 'right' },
    { id: 'sources_never_run', labelId: 'monitoring.column.sourcesNeverRun', align: 'right' },
];

function compareMediaRows(a, b, orderBy) {
    switch (orderBy) {
        case 'sources_count':
        case 'sources_error_count':
        case 'sources_never_run':
            return compareNumbers(a?.[orderBy], b?.[orderBy]);
        case 'name':
        default:
            return compareStrings(a?.name, b?.name);
    }
}

export default function MediaSummaryTable({ medias }) {
    const intl = useIntl();
    const navigate = useNavigate();
    const [orderBy, setOrderBy] = useState('name');
    const [order, setOrder] = useState('asc');

    const handleSort = (columnId) => {
        const isAsc = orderBy === columnId && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(columnId);
    };

    const sortedMedias = useMemo(() => {
        return [...(medias ?? [])].sort((a, b) =>
            applySortDirection(compareMediaRows(a, b, orderBy), order)
        );
    }, [medias, order, orderBy]);

    const headerCellSx = {
        fontWeight: 600,
        color: 'text.secondary',
        borderColor: 'divider',
        bgcolor: 'background.paper',
    };

    return (
        <TableContainer
            sx={{
                height: '100%',
                overflow: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
            }}
        >
            <Table
                stickyHeader
                size="small"
                aria-label={intl.formatMessage({ id: 'monitoring.title' })}
            >
                <TableHead>
                    <TableRow>
                        {MEDIA_SORT_COLUMNS.map((column) => (
                            <SortableTableHeaderCell
                                key={column.id}
                                columnId={column.id}
                                label={intl.formatMessage({ id: column.labelId })}
                                orderBy={orderBy}
                                order={order}
                                onSort={handleSort}
                                align={column.align}
                                sx={headerCellSx}
                            />
                        ))}
                        <TableCell align="right" sx={headerCellSx} />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedMedias.map((media) => (
                        <TableRow
                            key={media.id}
                            hover
                            onClick={() => navigate(getMediaDetailPath(media.id))}
                            sx={{ cursor: 'pointer' }}
                        >
                            <TableCell sx={{ borderColor: 'divider' }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {media.name}
                                </Typography>
                            </TableCell>
                            <CountCell value={media.sources_count} />
                            <CountCell value={media.sources_error_count} color="error.main" />
                            <CountCell value={media.sources_never_run} color="warning.main" />
                            <TableCell align="right" sx={{ borderColor: 'divider' }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    endIcon={<ChevronRightIcon />}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        navigate(getMediaDetailPath(media.id));
                                    }}
                                >
                                    {intl.formatMessage({ id: 'monitoring.column.viewSources' })}
                                </Button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
